/**
 * Browser-only Canvas I/O helpers.
 *
 * This module is the single place that touches HTML5 Canvas / File APIs, which
 * keeps every scoring & repair algorithm pure and testable elsewhere.
 *
 * Important: drawing an image onto a canvas and re-encoding it with `toBlob`
 * strips all EXIF metadata (camera model, GPS, orientation) by spec - the
 * byte-level `stripExif` pass in the exporter is a second, explicit guarantee.
 */

import { resizePixels } from './image-auditor.ts';

/** Decodes any browser-supported image file into raw RGBA pixels. */
export async function fileToImageData(file: File): Promise<ImageData> {
  const drawable = await decodeToDrawable(file);
  try {
    const width = 'naturalWidth' in drawable && drawable.naturalWidth > 0 ? drawable.naturalWidth : drawable.width;
    const height = 'naturalHeight' in drawable && drawable.naturalHeight > 0 ? drawable.naturalHeight : drawable.height;
    return drawableToImageData(drawable, width, height);
  } finally {
    if ('close' in drawable && typeof drawable.close === 'function') drawable.close();
  }
}

type Drawable = ImageBitmap | HTMLImageElement;

async function decodeToDrawable(file: File): Promise<Drawable> {
  // createImageBitmap handles the common raster formats efficiently.
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch {
      // fall through to the <img> path (SVG / exotic containers)
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Draws a decoded image at its natural size and reads back the pixel buffer. */
export function drawableToImageData(drawable: Drawable, width: number, height: number): ImageData {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = getContext2d(canvas);
  ctx.drawImage(drawable, 0, 0, width, height);
  return ctx.getImageData(0, 0, width, height);
}

export function getContext2d(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas 2D context unavailable in this browser.');
  return ctx;
}

/**
 * Writes a raw RGBA buffer onto a canvas context.
 * Uses `createImageData` + `set` (instead of the ImageData constructor) so the
 * code stays compatible with TS 5.7+'s generic TypedArray definitions.
 */
export function putPixelsAt(
  ctx: CanvasRenderingContext2D,
  pixels: Uint8ClampedArray,
  width: number,
  height: number
): void {
  const image = ctx.createImageData(width, height);
  image.data.set(pixels);
  ctx.putImageData(image, 0, 0);
}

/** Materialises a pixel buffer on a live `<canvas>` element. */
export function putPixels(canvas: HTMLCanvasElement, pixels: Uint8ClampedArray, width: number, height: number): void {
  canvas.width = width;
  canvas.height = height;
  putPixelsAt(getContext2d(canvas), pixels, width, height);
}

/**
 * Scales pixel data down/up to fit inside `maxW` x `maxH` without cropping,
 * preserving aspect ratio (box filter).
 */
export function scaleToFit(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  maxW: number,
  maxH: number
): { pixels: Uint8ClampedArray; width: number; height: number } {
  const scale = Math.min(maxW / width, maxH / height, 1);
  const w = Math.max(1, Math.round(width * scale));
  const h = Math.max(1, Math.round(height * scale));
  if (w === width && h === height) return { pixels, width, height };
  return resizePixels(pixels, width, height, w, h);
}

/** Encodes a pixel buffer to a compressed image blob (JPEG/WebP/PNG). */
export async function pixelsToBlob(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  type: 'image/jpeg' | 'image/webp' | 'image/png' = 'image/jpeg',
  quality = 0.92
): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = getContext2d(canvas);
  // JPEG has no alpha channel - paint white first so transparency never turns black.
  if (type === 'image/jpeg') {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
  }
  putPixelsAt(ctx, pixels, width, height);

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality));
  if (!blob) throw new Error(`Canvas encoding to ${type} failed.`);
  return blob;
}

/** Blob -> Uint8Array (for ZIP entries / byte inspection). */
export async function blobToBytes(blob: Blob): Promise<Uint8Array> {
  const buffer = await blob.arrayBuffer();
  return new Uint8Array(buffer);
}

/** Triggers a browser download for an in-memory file. */
export function downloadBytes(bytes: Uint8Array, filename: string, type: string): void {
  const copy = new Uint8Array(bytes.length);
  copy.set(bytes);
  const blob = new Blob([copy], { type });
  downloadBlob(blob, filename);
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Give the browser a moment to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
